<template>
  <div
    :class="[
      bem.b(),
      bem.is('disabled', disabled),
      bem.is('controls-at-right', controlsAtRight),
      bem.m(size)
    ]"
  >
    <span
      v-if="!controlsAtRight"
      :class="[bem.e('decrease'), bem.is('disabled', decreaseDisabled)]"
      role="button"
      @click="decrease"
    >
      <cms-icon>
        <component :is="slots.decrease || 'minus'"></component>
      </cms-icon>
    </span>
    <span
      v-if="!controlsAtRight"
      :class="[bem.e('increase'), bem.is('disabled', increaseDisabled)]"
      role="button"
      @click="increase"
    >
      <cms-icon>
        <component :is="slots.increase || 'plus'"></component>
      </cms-icon>
    </span>
    <input
      ref="input"
      :class="bem.e('input')"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :max="max"
      :min="min"
      :step="step"
      :value="displayValue"
      @input="handleInput"
      @change="handleChange"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown.up.prevent="increase"
      @keydown.down.prevent="decrease"
    />
    <div v-if="controlsAtRight" :class="bem.e('controls')">
      <span
        :class="[bem.e('increase'), bem.is('disabled', increaseDisabled)]"
        role="button"
        @click="increase"
      >
        <cms-icon>
          <component :is="slots.increase || 'arrow-up'"></component>
        </cms-icon>
      </span>
      <span
        :class="[bem.e('decrease'), bem.is('disabled', decreaseDisabled)]"
        role="button"
        @click="decrease"
      >
        <cms-icon>
          <component :is="slots.decrease || 'arrow-down'"></component>
        </cms-icon>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'cms-input-number',
  inheritAttrs: false
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { createNamespace } from '@chenms-zj/utils/create'
import { inputNumberEmits, inputNumberProps } from './input-number'
import { useSlots } from 'vue'

const bem = createNamespace('input-number')

const props = defineProps(inputNumberProps)
const emit = defineEmits(inputNumberEmits)
const slots: any = useSlots()

const input = ref<HTMLInputElement | null>(null)
const currentValue = ref(props.modelValue)

const displayValue = computed(() => {
  if (currentValue.value === undefined || currentValue.value === null) {
    return ''
  }
  return currentValue.value.toString()
})

const minDisabled = computed(
  () => props.min !== undefined && currentValue.value <= props.min
)
const maxDisabled = computed(
  () => props.max !== undefined && currentValue.value >= props.max
)
const decreaseDisabled = computed(() => props.disabled || minDisabled.value)
const increaseDisabled = computed(() => props.disabled || maxDisabled.value)

const controlsAtRight = computed(
  () => props.controls && props.controlsPosition === 'right'
)

const increase = () => {
  if (increaseDisabled.value) return
  const value = Number(currentValue.value) + Number(props.step)
  setCurrentValue(value)
}

const decrease = () => {
  if (decreaseDisabled.value) return
  const value = Number(currentValue.value) - Number(props.step)
  setCurrentValue(value)
}

const setCurrentValue = (value: number) => {
  let newVal = value

  if (props.stepStrictly) {
    const stepPrecision = (props.step.toString().split('.')[1] || '').length
    newVal = Math.round(newVal / props.step) * props.step
    if (stepPrecision > 0) {
      newVal = parseFloat(newVal.toFixed(stepPrecision))
    }
  }

  if (props.precision !== undefined) {
    newVal = parseFloat(newVal.toFixed(props.precision))
  }

  if (props.min !== undefined) {
    newVal = Math.max(props.min, newVal)
  }
  if (props.max !== undefined) {
    newVal = Math.min(props.max, newVal)
  }

  currentValue.value = newVal
  emit('update:modelValue', newVal)
  emit('change', newVal)
}

const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  const newVal = value === '' ? undefined : Number(value)
  if (!isNaN(newVal)) {
    setCurrentValue(newVal)
  }
}

const handleChange = () => {
  emit('change', currentValue.value)
}

const handleBlur = (event: FocusEvent) => {
  emit('blur', event)
}

const handleFocus = (event: FocusEvent) => {
  emit('focus', event)
}

watch(
  () => props.modelValue,
  newVal => {
    currentValue.value = newVal
  }
)
</script>

<style scoped lang="scss">
.cms-input-number {
  position: relative;
  display: inline-flex;
  width: 180px;
  line-height: 38px;

  &__input {
    background-color: var(--cms-fill-color-blank);
    background-image: none;
    border-radius: 4px;
    border: 1px solid var(--cms-border-color);
    box-sizing: border-box;
    color: var(--cms-text-color-regular);
    display: inline-block;
    font-size: inherit;
    height: 40px;
    line-height: 40px;
    outline: none;
    padding: 0 15px;
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
    width: 100%;
    text-align: center;

    &:focus {
      outline: none;
      border-color: var(--cms-color-primary);
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &::placeholder {
      color: var(--cms-text-color-placeholder);
    }

    &:hover {
      border-color: var(--cms-border-color-hover);
    }
  }

  &__decrease,
  &__increase {
    position: absolute;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    color: var(--cms-text-color-regular);
    cursor: pointer;
    transition: all 0.3s;
    user-select: none;
    background-color: var(--cms-fill-color-light);
    border: 1px solid var(--cms-border-color);

    .cms-icon {
      width: 12px;
      height: 12px;
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    &:hover {
      color: var(--cms-color-primary);
      border-color: var(--cms-color-primary);

      & + .cms-input-number__input {
        border-color: var(--cms-color-primary);
      }
    }

    &.is-disabled {
      color: var(--cms-disabled-text-color);
      cursor: not-allowed;
      background-color: var(--cms-disabled-bg-color);
      border-color: var(--cms-border-color-light);

      &:hover {
        color: var(--cms-disabled-text-color);
        border-color: var(--cms-border-color-light);
      }
    }
  }

  &:not(.is-controls-right) {
    .cms-input-number__decrease,
    .cms-input-number__increase {
      width: 40px;
      height: 100%;
      box-sizing: border-box;
    }

    .cms-input-number__decrease {
      left: 0;
      border-right: 0;
      border-radius: 4px 0 0 4px;
    }

    .cms-input-number__increase {
      right: 0;
      border-left: 0;
      border-radius: 0 4px 4px 0;
    }

    .cms-input-number__input {
      padding: 0 50px;
    }
  }

  &.is-controls-right {
    .cms-input-number__decrease,
    .cms-input-number__increase {
      height: 19px;
      line-height: 19px;
      width: 22px;
      right: 1px;
      border-left: 1px solid var(--cms-border-color);
    }

    .cms-input-number__decrease {
      bottom: 1px;
      border-top: 1px solid var(--cms-border-color);
      border-bottom-right-radius: 4px;
    }

    .cms-input-number__increase {
      top: 1px;
      border-bottom: 1px solid var(--cms-border-color);
      border-top-right-radius: 4px;
    }

    .cms-input-number__input {
      padding-right: 50px;
    }
  }

  &.is-disabled {
    .cms-input-number__input {
      background-color: var(--cms-disabled-bg-color);
      border-color: var(--cms-border-color-light);
      color: var(--cms-disabled-text-color);
      cursor: not-allowed;

      &:hover {
        border-color: var(--cms-border-color-light);
      }
    }

    .cms-input-number__decrease,
    .cms-input-number__increase {
      background-color: var(--cms-disabled-bg-color);
      border-color: var(--cms-border-color-light);
      color: var(--cms-disabled-text-color);
      cursor: not-allowed;
    }
  }

  &--large {
    width: 200px;
    line-height: 42px;

    .cms-input-number__input {
      height: 44px;
      line-height: 44px;
      font-size: 14px;
    }

    &.is-controls-right {
      .cms-input-number__decrease,
      .cms-input-number__increase {
        width: 26px;
        height: 21px;
        line-height: 21px;
      }
    }
  }

  &--small {
    width: 130px;
    line-height: 30px;

    .cms-input-number__input {
      height: 32px;
      line-height: 32px;
      font-size: 13px;
    }

    &.is-controls-right {
      .cms-input-number__decrease,
      .cms-input-number__increase {
        width: 20px;
        height: 15px;
        line-height: 15px;
      }
    }

    &:not(.is-controls-right) {
      .cms-input-number__decrease,
      .cms-input-number__increase {
        width: 32px;
      }

      .cms-input-number__input {
        padding: 0 42px;
      }
    }
  }

  &.is-without-controls {
    .cms-input-number__input {
      padding: 0 15px;
    }
  }
}
</style>
